import React, { useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useDashboardState } from "components/DashboardContext";
import "./SidebarDropdown.scss";
import UniversalInput from "components/uiComponents/UniversalInput";
import UniversalCheckbox from "components/uiComponents/UniversalCheckbox";
import { ReactComponent as SearchIcon } from "static/icons/search-grey.svg";
import classNames from "classnames";
import { valueTypes } from "utils/builder/ResultClasses"

const SidebarDropdown = ({
  commonOptions = [],
  uncommonOptions = [],
  customOptions = [],
  selectedItems,
  optionsType,
  onChangeSelectedItems,
  hasSelectedCategories = true,
}) => {
  const { t } = useTranslation();

  const {
    setCheckSidebarDropdownStatus,
    getLabelByIsTranslateModelEnabled
  } = useDashboardState();

  const [search, setSearch] = useState('');
  const [isUncommonVisible, setIsUncommonVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('common');

  const isTabsEnabled = optionsType === 'properties' || optionsType === 'groups';

  const isCustomType = (type) =>
    type >= valueTypes.customNumber && type <= valueTypes.customEnumMulty;
  const isProType = (type) =>
    type >= valueTypes.proNumber && type <= valueTypes.proEnumMulty;

  const customParams = useMemo(
    () => (customOptions || []).filter(p => isCustomType(p?.type)),
    [customOptions]
  );
  const proParams = useMemo(
    () => (customOptions || []).filter(p => isProType(p?.type)),
    [customOptions]
  );

  const buildOptions = (opts, { getLabel, getSelectKey, getName }) => {
    const seen = new Set();
    const res = [];
    (opts || []).forEach(item => {
      const label = String(getLabel(item) ?? '');
      const firstChar = label.charAt(0);
      const showChar = seen.has(firstChar) ? null : (seen.add(firstChar), firstChar);
      const selectKey = getSelectKey(item, label);
      const inputName = getName ? getName(item, label) : String(selectKey);
      res.push({
        label,
        selectKey,
        name: inputName,
        firstChar: showChar,
      });
    });
    return res;
  };

  const commonOptionsUpd = useMemo(
    () =>
      buildOptions(commonOptions, {
        getLabel: (i) => getLabelByIsTranslateModelEnabled(i),
        getSelectKey: (i) => i,
        getName: (i, lbl) => String(i) || lbl,
      }),
    [commonOptions, getLabelByIsTranslateModelEnabled]
  );

  const uncommonOptionsUpd = useMemo(
    () =>
      buildOptions(uncommonOptions, {
        getLabel: (i) => getLabelByIsTranslateModelEnabled(i),
        getSelectKey: (i) => i,
        getName: (i, lbl) => String(i) || lbl,
      }),
    [uncommonOptions, getLabelByIsTranslateModelEnabled]
  );

  const customOptionsUpd = useMemo(
    () =>
      buildOptions(customParams, {
        getLabel: (p) => p?.name ?? '',
        getSelectKey: (_p, lbl) => lbl,
        getName: (_p, lbl) => lbl,
      }),
    [customParams]
  );

  const proOptionsUpd = useMemo(
    () =>
      buildOptions(proParams, {
        getLabel: (p) => p?.name ?? '',
        getSelectKey: (_p, lbl) => lbl,
        getName: (_p, lbl) => lbl,
      }),
    [proParams]
  );

  const renderOptions = (list) => {
    return (
      <div
        className={classNames({
          'sidebar-dropdown__options': true,
          'sidebar-dropdown__options_common': isUncommonVisible,
        })}
      >
        {list.map(option => {
          const handleCheckboxChange = () => {
            onChangeSelectedItems(option.selectKey, optionsType);
          };
          return (
            <div key={String(option.selectKey)} className="sidebar-dropdown__option_wrapper">
              <div className="sidebar-dropdown__option_char">
                {option.firstChar}
              </div>
              <div className="sidebar-dropdown__option">
                <UniversalCheckbox
                  name={option.name}
                  label={option.label}
                  onChange={handleCheckboxChange}
                  checked={selectedItems?.some(item => item === option.selectKey)}
                />
              </div>
            </div>
          );
        })}
      </div>
    )
  };

  const allOptionsForSearch = useMemo(() => {
    if (isTabsEnabled) return [];
    return [
      ...commonOptionsUpd,
      ...uncommonOptionsUpd,
      ...customOptionsUpd,
      ...proOptionsUpd,
    ].filter(Boolean);
  }, [isTabsEnabled, commonOptionsUpd, uncommonOptionsUpd, customOptionsUpd, proOptionsUpd]);

  const searchedOptions = useMemo(() => {
    if (!search) return null;
    const s = search.toLowerCase();

    let base = [];
    if (isTabsEnabled) {
      if (activeTab === 'common') {
        base = [...commonOptionsUpd, ...uncommonOptionsUpd];
      } else if (activeTab === 'custom') {
        base = customOptionsUpd;
      } else if (activeTab === 'pro') {
        base = proOptionsUpd;
      }
    } else {
      base = allOptionsForSearch;
    }

    return base.filter(o => String(o.label).toLowerCase().includes(s));
  }, [search, activeTab, isTabsEnabled, commonOptionsUpd, uncommonOptionsUpd, customOptionsUpd, proOptionsUpd, allOptionsForSearch]);

  const handleSearchChange = value => setSearch(value);

  const handleSwitcherClick = () => {
    setIsUncommonVisible(!isUncommonVisible);
    setCheckSidebarDropdownStatus(true);
  };

  const handleClearSearch = () => setSearch('');

  return (
    <>
      <div className="sidebar-dropdown">
        {isTabsEnabled && (
          <div className="sidebar-dropdown__tabs">
            <button
              type="button"
              className={classNames('sidebar-dropdown__tab', { 'is-active': activeTab === 'common' })}
              onClick={() => setActiveTab('common')}
            >
              {t('model_parameters')}
            </button>
            <button
              type="button"
              className={classNames('sidebar-dropdown__tab', { 'is-active': activeTab === 'custom' })}
              onClick={() => setActiveTab('custom')}
            >
              {t('custom_parameters')}
            </button>
            <button
              type="button"
              className={classNames('sidebar-dropdown__tab', { 'is-active': activeTab === 'pro' })}
              onClick={() => setActiveTab('pro')}
            >
              {t('pro_parameters')}
            </button>
          </div>
        )}

        <div className="sidebar-dropdown_padding-container">
          <div className="sidebar-dropdown__search_container">
            <UniversalInput
              leftIcon={<SearchIcon className="sidebar-dropdown__search_icon" />}
              name="search"
              type="text"
              id="search"
              placeholder={t('search')}
              onChangeValue={handleSearchChange}
              value={search}
              isClearButtonActive
              onClearSearch={handleClearSearch}
              required
            />
          </div>

          <div className="sidebar-dropdown__options_container">
            {!!search && searchedOptions && renderOptions(searchedOptions)}

            {!search && (
              <>
                {isTabsEnabled ? (
                  <>
                    {activeTab === 'custom' && !!customOptionsUpd.length && renderOptions(customOptionsUpd)}

                    {activeTab === 'pro' && !!proOptionsUpd.length && renderOptions(proOptionsUpd)}

                    {activeTab === 'common' && !!commonOptionsUpd.length && (
                      <>
                        <div className="sidebar-dropdown__section-title">
                          {t('most_popular')}
                        </div>
                        {renderOptions(commonOptionsUpd)}

                        {isUncommonVisible && !!uncommonOptionsUpd.length && (
                          <>
                            <div className="sidebar-dropdown__options_line" />
                            {renderOptions(uncommonOptionsUpd)}
                          </>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {!!commonOptionsUpd.length && (
                      <>
                        <div className="sidebar-dropdown__section-title">
                          {t('most_popular')}
                        </div>
                        {renderOptions(commonOptionsUpd)}

                        {isUncommonVisible && !!uncommonOptionsUpd.length && (
                          <>
                            <div className="sidebar-dropdown__options_line" />
                            {renderOptions(uncommonOptionsUpd)}
                          </>
                        )}
                      </>
                    )}

                    {!!customOptionsUpd.length && renderOptions(customOptionsUpd)}
                    {!!proOptionsUpd.length && renderOptions(proOptionsUpd)}
                  </>
                )}
              </>
            )}
          </div>

          {!search &&
            activeTab === 'common' &&
            !!uncommonOptionsUpd.length &&
            !!commonOptionsUpd.length && (
              <div
                className="sidebar-dropdown__options_switcher"
                onClick={handleSwitcherClick}
              >
                {isUncommonVisible ? t('show_less') : t('show_all')}
              </div>
            )}
        </div>
      </div>
    </>
  );
};

export default SidebarDropdown;